import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { FOCUS_AREAS } from "@/lib/focusAreas";

export type ResourceRow = {
  id: string;
  partner_org_id: string;
  created_by: string;
  title: string;
  description: string | null;
  resource_type: "link" | "file" | string;
  url: string | null;
  file_path: string | null;
  focus_area: string | null;
  counties: string[];
  published: boolean;
  created_at: string;
  updated_at: string;
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  orgId: string;
  editing: ResourceRow | null;
  onSaved: () => void;
};

export default function ResourceFormDialog({ open, onOpenChange, orgId, editing, onSaved }: Props) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<"link" | "file">("link");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [existingFilePath, setExistingFilePath] = useState<string | null>(null);
  const [focusArea, setFocusArea] = useState<string>("");
  const [published, setPublished] = useState(true);

  useEffect(() => {
    if (open) {
      setTitle(editing?.title ?? "");
      setDescription(editing?.description ?? "");
      setType((editing?.resource_type as any) ?? "link");
      setUrl(editing?.url ?? "");
      setFile(null);
      setExistingFilePath(editing?.file_path ?? null);
      setFocusArea(editing?.focus_area ?? "");
      setPublished(editing?.published ?? true);
    }
  }, [open, editing]);

  const save = useMutation({
    mutationFn: async () => {
      if (!title.trim()) throw new Error("Title is required");
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth.user?.id;
      if (!uid) throw new Error("Sign in required");

      let filePath: string | null = existingFilePath;
      let resolvedUrl: string | null = type === "link" ? (url.trim() || null) : null;

      if (type === "file" && file) {
        const ext = file.name.split(".").pop() ?? "bin";
        const path = `${orgId}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("partner-resources")
          .upload(path, file, { contentType: file.type, upsert: false });
        if (upErr) throw upErr;
        // Remove the old file if we replaced it
        if (existingFilePath && existingFilePath !== path) {
          await supabase.storage.from("partner-resources").remove([existingFilePath]);
        }
        filePath = path;
        resolvedUrl = null;
      }

      if (type === "link" && existingFilePath && !file) {
        // switched from file -> link, clear the old file
        await supabase.storage.from("partner-resources").remove([existingFilePath]);
        filePath = null;
      }

      if (type === "link" && !resolvedUrl) throw new Error("URL is required for link resources");
      if (type === "file" && !filePath) throw new Error("Please choose a file to upload");

      const payload = {
        partner_org_id: orgId,
        created_by: uid,
        title: title.trim(),
        description: description.trim() || null,
        resource_type: type,
        url: resolvedUrl,
        file_path: filePath,
        focus_area: focusArea || null,
        counties: editing?.counties ?? [],
        published,
      };

      if (editing) {
        const { error } = await supabase.from("partner_resources").update(payload).eq("id", editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("partner_resources").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(editing ? "Resource updated" : "Resource added");
      onSaved();
      onOpenChange(false);
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to save"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit resource" : "Add resource"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
          </div>
          <div>
            <Label>Short description</Label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="link">External link</SelectItem>
                  <SelectItem value="file">File upload</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Focus area</Label>
              <Select value={focusArea || "_none"} onValueChange={(v) => setFocusArea(v === "_none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {FOCUS_AREAS.map((fa) => (
                    <SelectItem key={fa} value={fa}>{fa}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {type === "link" ? (
            <div>
              <Label>URL</Label>
              <Input type="url" placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} />
            </div>
          ) : (
            <div>
              <Label>File</Label>
              <div className="mt-1 flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm border border-border rounded-md px-3 py-2 cursor-pointer hover:bg-muted/30">
                  <Upload className="h-4 w-4" />
                  <span>{file ? file.name : existingFilePath ? "Replace file…" : "Choose file"}</span>
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                </label>
                {existingFilePath && !file && (
                  <span className="text-xs text-muted-foreground truncate">Current: {existingFilePath.split("/").pop()}</span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Stored in your private partner workspace; published files are shared via short-lived signed links.</p>
            </div>
          )}

          <label className="flex items-center justify-between gap-3 border-t border-border pt-3">
            <div>
              <div className="text-sm font-medium">Publish</div>
              <div className="text-xs text-muted-foreground">Show on your public partner profile.</div>
            </div>
            <Switch checked={published} onCheckedChange={setPublished} />
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editing ? "Save changes" : "Add resource"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}